import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { GoogleAuthService } from '../../../services/google-auth.service';
import { BlogLayout, LayoutPreferenceService } from '../../../services/layout-preference.service';

interface LayoutOption {
  value: BlogLayout;
  label: string;
  icon: string;
  hint: string;
}

const OPTIONS: LayoutOption[] = [
  { value: 'inline',          label: 'Inline expand', icon: 'view_agenda',  hint: 'Featured on top, others expand inline' },
  { value: 'drawer-featured', label: 'Side drawer',   icon: 'view_sidebar', hint: 'Featured on top, others open in a drawer' },
  { value: 'drawer-grid',     label: 'Card grid',     icon: 'grid_view',    hint: 'All posts as cards, drawer for detail' },
];

@Component({
  selector: 'app-layout-switcher',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <ng-container *ngIf="auth.isAdmin()">
      <div class="ls" (focusin)="open = true" (focusout)="onBlur($event)">
        <button type="button" class="ls__btn" (click)="toggle()" [attr.aria-expanded]="open" aria-haspopup="menu">
          <span class="material-icons" aria-hidden="true">{{ activeIcon() }}</span>
          <span class="ls__label">{{ activeLabel() }}</span>
          <span class="material-icons ls__chev" aria-hidden="true">expand_more</span>
        </button>
        <div class="ls__menu" role="menu" *ngIf="open">
          <button *ngFor="let opt of options"
                  type="button"
                  role="menuitemradio"
                  class="ls__opt"
                  [class.ls__opt--active]="opt.value === current"
                  [attr.aria-checked]="opt.value === current"
                  (click)="select(opt.value)">
            <span class="material-icons ls__optIcon" aria-hidden="true">{{ opt.icon }}</span>
            <span class="ls__optBody">
              <span class="ls__optLabel">{{ opt.label }}</span>
              <span class="ls__optHint">{{ opt.hint }}</span>
            </span>
            <span *ngIf="opt.value === current" class="material-icons ls__check" aria-hidden="true">check</span>
          </button>
        </div>
      </div>
    </ng-container>
  `,
  styleUrls: ['./layout-switcher.component.css'],
})
export class LayoutSwitcherComponent implements OnInit, OnDestroy {
  options = OPTIONS;
  current: BlogLayout = 'inline';
  open = false;

  auth = inject(GoogleAuthService);
  private prefs = inject(LayoutPreferenceService);
  private sub?: Subscription;

  ngOnInit(): void {
    this.sub = this.prefs.layout$.subscribe(l => (this.current = l));
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  toggle(): void { this.open = !this.open; }

  select(v: BlogLayout): void {
    this.prefs.set(v);
    this.open = false;
  }

  onBlur(evt: FocusEvent): void {
    const next = evt.relatedTarget as Node | null;
    const root = (evt.currentTarget as HTMLElement);
    if (next && root.contains(next)) return;
    this.open = false;
  }

  activeIcon(): string {
    return this.options.find(o => o.value === this.current)?.icon ?? 'view_agenda';
  }
  activeLabel(): string {
    return this.options.find(o => o.value === this.current)?.label ?? 'Layout';
  }
}
